import React from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCenter,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  MeasuringStrategy,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Utility for screen reader announcements
const announce = (msg: string) => {
  const el = document.getElementById('pivot-announcements');
  if (el) el.textContent = msg;
};

// Zone names in Greek for announcements
const ZONE_NAMES_GREEK = {
  available: 'Διαθέσιμα',
  rows: 'Γραμμές',
  cols: 'Στήλες',
} as const;

export interface Dimension {
  id: string;
  label: string;
}

export interface PivotConfig {
  available: Dimension[];
  rows: Dimension[];
  cols: Dimension[];
}

export interface PivotConfiguratorProps {
  dimensions: Dimension[];
  config: PivotConfig;
  onChange: (config: PivotConfig) => void;
  className?: string;
}

interface DraggableChipProps {
  dimension: Dimension;
  zone: 'available' | 'rows' | 'cols';
  disabled?: boolean;
}

const DraggableChip = React.memo(function DraggableChip({
  dimension,
  zone,
  disabled = false,
}: DraggableChipProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `${zone}-${dimension.id}`,
    data: { dimension, zone },
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition, // Disable transition while dragging
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`pivot-chip ${isDragging ? 'pivot-chip-dragging' : ''}`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-grabbed={isDragging}
      aria-disabled={disabled}
      aria-describedby="pivot-announcements"
      data-dragging={isDragging}
    >
      {dimension.label}
    </div>
  );
});

export interface DropZoneProps {
  title: string;
  dimensions: Dimension[];
  zone: 'available' | 'rows' | 'cols';
  className?: string;
  disabled?: boolean;
}

function DropZone({ title, dimensions, zone, className = '', disabled = false }: DropZoneProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: zone,
    disabled,
  });

  const sortableIds = React.useMemo(
    () => dimensions.map((d) => `${zone}-${d.id}`),
    [dimensions, zone],
  );

  // Extract count from title if present (e.g., "Fields (3)" -> "3")
  const countMatch = title.match(/\((\d+)\)$/);
  const displayTitle = countMatch ? title.replace(/\s*\(\d+\)$/, '') : title;
  const count = countMatch ? countMatch[1] : dimensions.length.toString();

  return (
    <div
      ref={setNodeRef}
      className={`pivot-zone ${className} ${isOver ? 'pivot-zone-over' : ''}`}
      role="region"
      aria-label={`περιοχή απόθεσης ${displayTitle}`}
      data-busy={disabled}
    >
      <h4 className="pivot-zone-title">
        {displayTitle}
        {dimensions.length > 0 && <span className="pivot-zone-count">{count}</span>}
      </h4>
      <div className="pivot-zone-content">
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          {dimensions.map((dimension) => (
            <DraggableChip
              key={dimension.id}
              dimension={dimension}
              zone={zone}
              disabled={disabled}
            />
          ))}
        </SortableContext>
        {dimensions.length === 0 && <div className="pivot-zone-empty">Αποθέστε πεδία εδώ</div>}
      </div>
    </div>
  );
}

// Export types and components
export { DropZone };

export function PivotConfigurator({
  dimensions,
  config,
  onChange,
  className = '',
}: PivotConfiguratorProps) {
  const [activeDimension, setActiveDimension] = React.useState<Dimension | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 120,
        tolerance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const data = active.data.current;
    if (data?.dimension) {
      setActiveDimension(data.dimension);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDimension(null);

    if (!over) return;

    const activeData = active.data.current;
    if (!activeData?.dimension || !activeData?.zone) return;

    const sourceDimension = activeData.dimension;
    const sourceZone = activeData.zone;

    // Parse active.id to get source info
    const activeIdStr = String(active.id);
    const [, dimensionId] = activeIdStr.split('-', 2);

    // Determine target zone and position
    let targetZone: 'available' | 'rows' | 'cols';
    let targetIndex: number;

    const overIdStr = String(over.id);

    // Check if over.id is a zone id (available, rows, cols)
    if (overIdStr === 'available' || overIdStr === 'rows' || overIdStr === 'cols') {
      // Dropped on empty zone - append to end
      targetZone = overIdStr as 'available' | 'rows' | 'cols';
      targetIndex = config[targetZone].length;
    } else if (overIdStr.includes('-')) {
      // Dropped on a chip - parse zone and find position
      const [overZone, overDimensionId] = overIdStr.split('-', 2);
      targetZone = overZone as 'available' | 'rows' | 'cols';
      targetIndex = config[targetZone].findIndex((d) => d.id === overDimensionId);
      if (targetIndex === -1) {
        targetIndex = config[targetZone].length; // fallback to end
      }
    } else {
      return; // Invalid drop target
    }

    // Create new config
    const newConfig = { ...config };

    // Remove from source
    const sourceArray = [...newConfig[sourceZone]];
    const sourceIndex = sourceArray.findIndex((d) => d.id === sourceDimension.id);
    if (sourceIndex >= 0) {
      sourceArray.splice(sourceIndex, 1);
      newConfig[sourceZone] = sourceArray;
    }

    // Add to target
    if (sourceZone === targetZone && sourceIndex >= 0) {
      // Reordering within same zone
      let adjustedTargetIndex = targetIndex;
      if (sourceIndex < targetIndex) {
        adjustedTargetIndex--; // Adjust for removal
      }
      const reorderedArray = arrayMove(config[sourceZone], sourceIndex, adjustedTargetIndex);
      newConfig[targetZone] = reorderedArray as Dimension[];
    } else {
      // Moving between zones
      const targetArray = [...newConfig[targetZone]];
      targetArray.splice(targetIndex, 0, sourceDimension);
      newConfig[targetZone] = targetArray;
    }

    onChange(newConfig);

    // Announce the move to screen readers in Greek
    const zoneNameGreek = ZONE_NAMES_GREEK[targetZone as keyof typeof ZONE_NAMES_GREEK];
    const finalIndex =
      sourceZone === targetZone && sourceIndex >= 0
        ? sourceIndex < targetIndex
          ? targetIndex
          : targetIndex + 1
        : targetIndex + 1;
    announce(
      `Μετακινήθηκε το "${sourceDimension.label}" στη θέση ${finalIndex} στις ${zoneNameGreek}`,
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className={`pivot-configurator ${className}`}>
        <div className="pivot-zones">
          <DropZone
            title="Διαθέσιμα Πεδία"
            dimensions={config.available}
            zone="available"
            className="pivot-zone-available"
          />

          <DropZone
            title="Γραμμές"
            dimensions={config.rows}
            zone="rows"
            className="pivot-zone-rows"
          />

          <DropZone
            title="Στήλες"
            dimensions={config.cols}
            zone="cols"
            className="pivot-zone-cols"
          />
        </div>

        <DragOverlay dropAnimation={null}>
          {activeDimension ? (
            <div className="pivot-chip pivot-chip-dragging">{activeDimension.label}</div>
          ) : null}
        </DragOverlay>
      </div>
    </DndContext>
  );
}
